/* Legends Path account storage. Load before dashboard and wait for .ready.
 * customerId names only a local cache; the same-origin Shopify proxy authenticates
 * every server request independently. No browser customer ID is sent to the API.
 */
(function (window) {
  "use strict";
  if (window.lpAccountStorage) return;
  var config = window.lpAccountStorageConfig || {};
  var KEYS = [
    "lp_sheet_url_v1", "lp_free_masterclass_unlocked_v1", "lp_watched_masterclasses_v1",
    "lp_presentation_seen_v1", "lp_cc_places_v1", "lp_discord_username_v1", "lp_synth_mode_v2", "lp_synth_base_v2",
    "lp_synth_mmr_v2", "lp_synth_role_v2", "lp_synth_games_v2", "lp_synth_baseline_v2",
    "lp_synth_sheet_ledger_v1", "lp_synth_history_v1", "lp_synth_riot_v1", "lp_synth_saison_v1",
    "lp_offre_reco_v1", "lp_ob_v1", "lp_guide_v1", "lp_synth_plan_mc_v1", "lp_synth_last_rank_v1",
    "lp_synth_saison_hist_v1", "lp_serie_v2", "lp_coach_next_v1", "lp_mc_vue",
    "lp_lead_role", "lp_lead_tier", "lp_intro_vue_v1", "lp_intro_profil_v1"
  ];
  var allow = Object.create(null);
  KEYS.forEach(function (key) { allow[key] = true; });
  // This transient retry marker includes a Riot identity; keep it local to the
  // signed-in account without sending device retry scheduling to the server.
  var localScoped = { lp_stats_attente_v1: true };
  var owner = String(config.customerId || "");
  var scoped = /^[1-9][0-9]*$/.test(owner);
  var endpoint = typeof config.endpoint === "string" ? config.endpoint : "/apps/lp-dashboard/state";
  var endpointOK = /^\/apps\/[a-zA-Z0-9_\-/]+$/.test(endpoint);
  var readOnly = config.readOnly === true;
  var enabled = scoped && config.enabled === true && endpointOK && !readOnly;
  // Shopify signs this consistency assertion together with the authenticated
  // customer. It is never used by the server as an authentication credential.
  var requestEndpoint = endpoint + "?expected_customer_id=" + encodeURIComponent(owner);
  var prefix = "lp_account_state_v1:" + owner;
  var claimKey = "lp_account_legacy_owner_v1";
  var nativeStorage = null;
  var persistent = true;
  var memory = Object.create(null);
  var snapshot = { owner: owner, version: 0, values: Object.create(null) };
  var dirty = Object.create(null);
  var observed = Object.create(null);
  var status = scoped ? "loading" : "guest";
  var statusCode = null;
  var writesFrozen = false;
  var initialized = false;
  var initialAccountConfirmed = false;
  var activeRequest = null;
  var timer = null;
  var retryDelay = 1000;
  var lastRefresh = 0;
  var seq = 0;
  var tab = Date.now().toString(36) + Math.random().toString(36).slice(2);
  try {
    nativeStorage = window.localStorage;
    var probe = prefix + ":probe:" + tab;
    if (!readOnly) {
      nativeStorage.setItem(probe, "1");
      nativeStorage.removeItem(probe);
    }
  } catch (e) { persistent = false; }

  function own(object, key) { return Object.prototype.hasOwnProperty.call(object, key); }
  function readRaw(key) {
    if (!persistent && own(memory, key)) return memory[key];
    if (nativeStorage) {
      try { return nativeStorage.getItem(key); } catch (e) { persistent = false; }
    }
    return own(memory, key) ? memory[key] : null;
  }
  function writeRaw(key, value) {
    memory[key] = value;
    if (nativeStorage && persistent) {
      try {
        if (value === null) nativeStorage.removeItem(key); else nativeStorage.setItem(key, value);
        return true;
      } catch (e) { persistent = false; }
    }
    return false;
  }
  function parse(value) { try { return JSON.parse(value); } catch (e) { return null; } }
  function normalizeEmail(value) { return typeof value === "string" ? value.trim().toLowerCase() : ""; }
  function baseValue(key) { return own(snapshot.values, key) ? snapshot.values[key] : null; }
  function localValue(key) { return own(dirty, key) ? dirty[key].value : baseValue(key); }
  function dirtyCount() { return Object.keys(dirty).length; }
  function legacyOwnership() {
    var claim = readRaw(claimKey);
    if (claim && claim !== owner) return "different";
    var expected = normalizeEmail(config.email);
    var emails = [];
    ["lp_ob_v1", "lp_guide_v1"].forEach(function (key) {
      var value = parse(readRaw(key));
      var email = value && normalizeEmail(value.email);
      if (email) emails.push(email);
    });
    if (emails.some(function (email) { return expected && email !== expected; })) return "different";
    if (expected && emails.length && emails.every(function (email) { return email === expected; })) return "matching";
    return claim === owner ? "matching" : "unknown";
  }
  function hasLegacy() {
    return enabled && status !== "signin" && status !== "error" && legacyOwnership() !== "different" && KEYS.some(function (key) { return readRaw(key) !== null; });
  }
  function getStatus() {
    return { status: status, code: statusCode, dirtyCount: dirtyCount(), persistent: persistent, hasLegacy: hasLegacy(), enabled: enabled, readOnly: readOnly, needsReload: writesFrozen };
  }
  function event(name, detail) {
    try { window.dispatchEvent(new window.CustomEvent(name, { detail: detail })); } catch (e) {}
  }
  function setStatus(next, extra) {
    status = next;
    statusCode = extra && extra.code ? extra.code : null;
    event("lp-account-storage-status", Object.assign(getStatus(), extra || {}));
  }
  function changed(keys, source) {
    if (!initialized || !keys.length) return;
    // Dashboard consumers keep some parsed state in closures. Once account
    // values advance remotely, stop those old closures from writing stale
    // objects while the integration drains pending changes and reloads.
    if (config.reloadOnRemoteChange === true && scoped) writesFrozen = true;
    event("lp-account-storage-remote-change", { keys: keys, source: source, dirtyCount: dirtyCount() });
  }
  function validSnapshot(value) {
    if (!value || value.owner !== owner || !Number.isSafeInteger(value.version) || value.version < 0 || !value.values || typeof value.values !== "object" || Array.isArray(value.values)) return null;
    var values = Object.create(null);
    KEYS.forEach(function (key) { if (typeof value.values[key] === "string") values[key] = value.values[key]; });
    return { owner: owner, version: value.version, values: values };
  }
  function loadSnapshot() {
    var value = validSnapshot(parse(readRaw(prefix)));
    if (value && value.version >= snapshot.version) snapshot = value;
  }
  function readDirty(key) {
    var value = parse(readRaw(prefix + ":dirty:" + key));
    if (value && value.owner === owner && typeof value.id === "string" && (value.value === null || typeof value.value === "string") && (value.previous === null || typeof value.previous === "string")) {
      dirty[key] = value;
    } else delete dirty[key];
  }
  function loadCache() {
    loadSnapshot();
    KEYS.forEach(readDirty);
  }
  function saveDirty(key, edit) {
    if (edit === null) {
      delete dirty[key];
      return writeRaw(prefix + ":dirty:" + key, null);
    }
    dirty[key] = edit;
    return writeRaw(prefix + ":dirty:" + key, JSON.stringify(edit));
  }
  function editValue(key, value) {
    if (writesFrozen || readOnly) return;
    var consumerValue = own(observed, key) ? observed[key] : localValue(key);
    var requested = value;
    loadSnapshot();
    readDirty(key);
    var mergedOtherTab = false;
    if (own(dirty, key) && dirty[key].id.indexOf(tab + ":") !== 0 && dirty[key].value !== value) {
      // Another tab can save its journal before this tab receives a storage
      // event. Merge against what this consumer actually read, not that newly
      // discovered journal, so the consumer's stale array cannot erase it.
      value = mergeRaw(consumerValue, value, dirty[key].value);
      mergedOtherTab = value !== requested;
    }
    observed[key] = requested;
    if (localValue(key) === value) {
      if (mergedOtherTab) changed([key], "merge");
      return;
    }
    // Even a reversion to the last confirmed value is a pending user action:
    // an earlier upload can still be in flight and must not erase the undo.
    saveDirty(key, { owner: owner, id: tab + ":" + (++seq), value: value, previous: baseValue(key) });
    if (status === "signin") setStatus("signin", { code: statusCode });
    else setStatus(enabled ? (dirtyCount() ? "saving" : "saved") : "offline");
    if (mergedOtherTab) changed([key], "merge");
    schedule(400);
  }

  /* Three-way merge preserves additions and explicit removals on concurrent
   * progress arrays. Objects merge independent fields; a simultaneous scalar
   * edit keeps this device's pending user action. */
  function equivalent(a, b) { return JSON.stringify(a) === JSON.stringify(b); }
  function itemID(value) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      var fields = ["id", "gameId", "matchId", "t", "timestamp", "date"];
      for (var i = 0; i < fields.length; i++) {
        if (typeof value[fields[i]] === "string" || typeof value[fields[i]] === "number") return fields[i] + ":" + String(value[fields[i]]);
      }
    }
    return JSON.stringify(value);
  }
  function mergeJSON(base, local, remote) {
    if (equivalent(local, remote)) return local;
    if (equivalent(local, base)) return remote;
    if (equivalent(remote, base)) return local;
    if (Array.isArray(local) && Array.isArray(remote) && (base === undefined || Array.isArray(base))) {
      var old = new Map(); var left = new Map(); var right = new Map();
      function index(values, target) {
        var occurrences = Object.create(null);
        values.forEach(function (value) {
          var id = itemID(value);
          // Imported sheet games may repeat identical stats without a match ID.
          // Preserve their multiplicity instead of collapsing distinct games.
          if (value && typeof value === "object" && id.charAt(0) === "{") {
            occurrences[id] = (occurrences[id] || 0) + 1;
            id += "#" + occurrences[id];
          }
          target.set(id, value);
        });
      }
      index(base || [], old); index(local, left); index(remote, right);
      var ids = Array.from(new Set(Array.from(right.keys()).concat(Array.from(left.keys()))));
      var merged = ids.reduce(function (result, id) {
        if (old.has(id) && (!left.has(id) || !right.has(id))) return result;
        var value = left.has(id) && right.has(id) ? mergeJSON(old.get(id), left.get(id), right.get(id)) : (left.has(id) ? left.get(id) : right.get(id));
        result.push(value); return result;
      }, []);
      if (merged.length && merged.every(function (value) { return value && typeof value === "object" && typeof value.t === "number" && Number.isFinite(value.t); })) {
        merged.sort(function (a, b) { return a.t - b.t; });
      }
      return merged;
    }
    var isObject = function (value) { return value && typeof value === "object" && !Array.isArray(value); };
    if (isObject(local) && isObject(remote) && (base === undefined || isObject(base))) {
      var out = Object.create(null);
      var keys = Object.keys(local).concat(Object.keys(remote));
      keys.forEach(function (key) {
        if (key === "__proto__" || key === "constructor" || key === "prototype") return;
        var value = mergeJSON(base ? base[key] : undefined, local[key], remote[key]);
        if (value !== undefined) out[key] = value;
      });
      return out;
    }
    return local;
  }
  function mergeRaw(previous, desired, remote) {
    if (desired === remote) return desired;
    if (desired === previous) return desired;
    if (remote === previous || desired === null || remote === null) return desired;
    try {
      var before = previous === null ? undefined : JSON.parse(previous);
      return JSON.stringify(mergeJSON(before, JSON.parse(desired), JSON.parse(remote)));
    } catch (e) { return desired; }
  }
  function applyServer(value, submitted) {
    if (!value || String(value.customerId || "") !== owner) throw new Error("account_changed");
    var next = validSnapshot({ owner: owner, version: value && value.version, values: value && value.values });
    if (!next) throw new Error("invalid_state");
    loadCache();
    if (next.version < snapshot.version) return;
    var before = Object.create(null);
    KEYS.forEach(function (key) { before[key] = localValue(key); });
    var conflicts = [];
    snapshot = next;
    writeRaw(prefix, JSON.stringify(snapshot));
    KEYS.forEach(function (key) {
      readDirty(key);
      if (!own(dirty, key)) return;
      var edit = dirty[key];
      var remote = baseValue(key);
      if (remote === edit.value) { saveDirty(key, null); return; }
      var sent = submitted && submitted[key];
      var newerAction = sent && sent.id !== edit.id;
      if (remote === edit.previous && !newerAction) return;
      var merged;
      if (newerAction) {
        // Reconcile the submitted action first, even when its request lost a
        // conflict. Then apply this device's newer delta relative to that
        // submitted generation. Undoing our own watched mark must remove it
        // while retaining an independent mark added on another device.
        var reconciled = mergeRaw(sent.previous, sent.value, remote);
        merged = mergeRaw(sent.value, edit.value, reconciled);
      } else merged = mergeRaw(edit.previous, edit.value, remote);
      conflicts.push(key);
      if (merged === remote) saveDirty(key, null);
      else saveDirty(key, { owner: owner, id: tab + ":" + (++seq), value: merged, previous: remote });
    });
    var keys = KEYS.filter(function (key) { return before[key] !== localValue(key); });
    if (conflicts.length) setStatus("conflict", { keys: conflicts });
    changed(keys, conflicts.length ? "merge" : "remote");
  }
  function schedule(delay) {
    if (!enabled || !initialized || !dirtyCount() || status === "signin") return;
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(function () { timer = null; flush(); }, delay);
  }
  function request(method, body) {
    return new Promise(function (resolve, reject) {
      var controller = typeof window.AbortController === "function" ? new window.AbortController() : null;
      var done = false;
      var timeout = window.setTimeout(function () {
        if (done) return;
        done = true;
        if (controller) controller.abort();
        reject(new Error("timeout"));
      }, 8000);
      var options = { method: method, credentials: "same-origin", cache: "no-store", redirect: "error", headers: { Accept: "application/json" } };
      if (controller) options.signal = controller.signal;
      if (body) { options.headers["Content-Type"] = "application/json"; options.body = JSON.stringify(body); }
      Promise.resolve().then(function () { return window.fetch(requestEndpoint, options); }).then(function (response) {
        if (response.status === 401 || response.status === 403) {
          // Only a recognized machine error changes the login-reload behavior.
          // A generic signature/configuration error must not trigger a loop.
          return Promise.resolve().then(function () { return response.json(); }).catch(function () { return null; }).then(function (value) {
            var error = new Error(value && value.error === "account_changed" ? "account_changed" : "signin");
            error.status = response.status;
            throw error;
          });
        }
        if (!response.ok && response.status !== 409) { var failure = new Error("http_" + response.status); failure.status = response.status; throw failure; }
        return response.json().then(function (value) { return { conflict: response.status === 409, value: value }; });
      }).then(function (value) {
        if (done) return;
        done = true; window.clearTimeout(timeout); resolve(value);
      }, function (error) {
        if (done) return;
        done = true; window.clearTimeout(timeout); reject(error);
      });
    });
  }
  function handleFailure(error) {
    if (error.message === "signin" || error.message === "account_changed") setStatus("signin", { code: error.message });
    else if (error.status && error.status >= 400 && error.status < 500) setStatus("error", { code: error.message });
    else {
      setStatus("offline");
      schedule(retryDelay);
      retryDelay = Math.min(retryDelay * 2, 60000);
    }
  }
  function flush() {
    if (!initialized) return facade.ready.then(flush);
    if (activeRequest) return activeRequest;
    loadCache();
    if (!enabled || !dirtyCount() || status === "signin") return Promise.resolve(getStatus());
    if (timer) { window.clearTimeout(timer); timer = null; }
    var changes = Object.keys(dirty).map(function (key) { return { key: key, value: dirty[key].value, previous: dirty[key].previous }; });
    var submitted = Object.create(null);
    Object.keys(dirty).forEach(function (key) { submitted[key] = Object.assign({}, dirty[key]); });
    setStatus("saving");
    activeRequest = request("PATCH", { version: snapshot.version, changes: changes }).then(function (result) {
      applyServer(result.value, submitted);
      retryDelay = 1000;
      setStatus(dirtyCount() ? "saving" : "saved");
      schedule(result.conflict ? 100 : 400);
    }).catch(handleFailure).then(function () {
      activeRequest = null;
      return getStatus();
    });
    return activeRequest;
  }
  function refresh() {
    if (!initialized) return facade.ready.then(refresh);
    if (activeRequest) return activeRequest;
    if (!enabled) return Promise.resolve(getStatus());
    lastRefresh = Date.now();
    activeRequest = request("GET").then(function (result) {
      applyServer(result.value);
      retryDelay = 1000;
      setStatus(dirtyCount() ? "saving" : "saved");
      schedule(100);
    }).catch(handleFailure).then(function () { activeRequest = null; return getStatus(); });
    return activeRequest;
  }
  function importMerge(existing, legacy) {
    if (existing === null) return legacy;
    try {
      var current = JSON.parse(existing); var old = JSON.parse(legacy);
      function fill(keep, recover) {
        if (Array.isArray(keep) && Array.isArray(recover)) return mergeJSON(undefined, keep, recover);
        if (keep && recover && typeof keep === "object" && typeof recover === "object" && !Array.isArray(keep) && !Array.isArray(recover)) {
          var out = Object.create(null);
          Object.keys(recover).concat(Object.keys(keep)).forEach(function (key) {
            if (key === "__proto__" || key === "constructor" || key === "prototype") return;
            out[key] = own(keep, key) ? fill(keep[key], recover[key]) : recover[key];
          });
          return out;
        }
        return keep;
      }
      return JSON.stringify(fill(current, old));
    } catch (e) { return existing; }
  }
  function importNow() {
    if (writesFrozen || !hasLegacy()) return false;
    loadCache();
    var originals = Object.create(null);
    var before = Object.create(null);
    KEYS.forEach(function (key) {
      var value = readRaw(key);
      if (value !== null) { originals[key] = value; before[key] = localValue(key); }
    });
    Object.keys(originals).forEach(function (key) { editValue(key, importMerge(localValue(key), originals[key])); });
    /* Never discard the legacy copy unless all account journals were saved. */
    if (persistent && writeRaw(claimKey, owner)) {
      Object.keys(originals).forEach(function (key) { if (readRaw(key) === originals[key]) writeRaw(key, null); });
    }
    changed(Object.keys(originals).filter(function (key) { return before[key] !== localValue(key); }), "merge");
    setStatus(enabled ? (dirtyCount() ? "saving" : "saved") : "offline");
    return true;
  }
  var facade = {
    getItem: function (key) {
      key = String(key);
      if (scoped && allow[key]) {
        var value = localValue(key);
        observed[key] = value;
        return value;
      }
      return readRaw(scoped && own(localScoped, key) ? prefix + ":local:" + key : key);
    },
    setItem: function (key, value) {
      key = String(key); value = String(value);
      if (readOnly && (allow[key] || own(localScoped, key))) return;
      // A completed migration removes all shared legacy progress but leaves
      // its ownership receipt. A new guest starting from that empty storage
      // is a fresh generation; do not let the old receipt block their future
      // account recovery. Existing shared progress always retains protection.
      if (!scoped && allow[key] && readRaw(claimKey) !== null && KEYS.every(function (legacyKey) { return readRaw(legacyKey) === null; })) writeRaw(claimKey, null);
      if (scoped && allow[key]) editValue(key, value);
      else writeRaw(scoped && own(localScoped, key) ? prefix + ":local:" + key : key, value);
    },
    removeItem: function (key) {
      key = String(key);
      if (readOnly && (allow[key] || own(localScoped, key))) return;
      if (scoped && allow[key]) editValue(key, null);
      else writeRaw(scoped && own(localScoped, key) ? prefix + ":local:" + key : key, null);
    },
    getStatus: getStatus,
    freezeWrites: function () {
      if (scoped) writesFrozen = true;
      event("lp-account-storage-status", getStatus());
      return getStatus();
    },
    hasLegacy: hasLegacy,
    flush: flush,
    refresh: refresh,
    importLegacy: function () {
      return facade.ready.then(function () {
        var imported = importNow();
        function settle(attempts) {
          return flush().then(function () {
            if (dirtyCount() && status === "saving" && attempts > 0) return settle(attempts - 1);
            return { imported: imported, saved: imported && enabled && !dirtyCount() && status === "saved" };
          });
        }
        return settle(3);
      });
    }
  };
  window.lpAccountStorage = facade;
  if (scoped) loadCache();
  facade.ready = (enabled ? request("GET").then(function (result) {
    applyServer(result.value);
    initialAccountConfirmed = true;
    setStatus(dirtyCount() ? "saving" : "saved");
  }).catch(handleFailure) : Promise.resolve()).then(function () {
    // A failed local claim (e.g. storage quota/private mode) can leave the
    // legacy copy behind permanently. Automatically importing it again would
    // resurrect progress the student later removed on their account. Only a
    // confirmed pristine account is eligible for automatic recovery. Existing
    // account data and offline starts require the explicit recovery action.
    if (enabled && initialAccountConfirmed && snapshot.version === 0 && Object.keys(snapshot.values).length === 0 && legacyOwnership() === "matching") importNow();
    initialized = true;
    if (!scoped) setStatus("guest");
    else if (!enabled) setStatus("offline", { code: readOnly ? "preview" : (endpointOK ? "disabled" : "invalid_endpoint") });
    else if (status !== "offline" && status !== "signin" && status !== "error") setStatus(dirtyCount() ? "saving" : "saved");
    schedule(100);
    return facade;
  });
  window.addEventListener("storage", function (e) {
    if (!scoped || !e.key || (e.key !== prefix && e.key.indexOf(prefix + ":dirty:") !== 0 && e.key !== claimKey)) return;
    var before = Object.create(null);
    KEYS.forEach(function (key) { before[key] = localValue(key); });
    loadCache();
    changed(KEYS.filter(function (key) { return before[key] !== localValue(key); }), "tab");
    if (enabled && status !== "signin") setStatus(dirtyCount() ? "saving" : "saved");
    schedule(300);
  });
  function foregroundRefresh() {
    if (!enabled || !initialized || (window.document && window.document.hidden)) return;
    if (status === "signin") refresh();
    else if (dirtyCount()) flush();
    else if (Date.now() - lastRefresh > 10000) refresh();
  }
  window.addEventListener("online", foregroundRefresh);
  window.addEventListener("focus", foregroundRefresh);
  window.addEventListener("pagehide", function () { if (dirtyCount()) flush(); });
  if (window.document) window.document.addEventListener("visibilitychange", foregroundRefresh);
})(window);

